import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { can, homePath, type Capability } from './capabilities.js';
import { useAuth } from './useAuth.js';

interface RequireCapabilityProps {
  /** The DEV-67 capability the page needs (auth/capabilities.ts). */
  capability: Capability;
  children: ReactNode;
}

export function RequireCapability({ capability, children }: RequireCapabilityProps): JSX.Element {
  const { user, loading } = useAuth();
  if (loading) {
    return <div />;
  }
  if (user === null) {
    return <Navigate to="/login" replace />;
  }
  if (!can(user.role, capability)) {
    return <Navigate to="/access-denied" replace />;
  }
  return <>{children}</>;
}

interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps): JSX.Element {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) {
    return <div />;
  }
  if (user === null) {
    return <Navigate to="/login" replace />;
  }
  if (user.mfaEnrollmentRequired) return <Navigate to="/login" replace />;
  // Finance has no dashboard; it opens on what it monitors.
  if (pathname === '/dashboard' && !can(user.role, 'console')) return <Navigate to={homePath(user.role)} replace />;
  return <>{children}</>;
}
